import { useState } from "react";
import { message } from "antd";
import {
  runPreflight, startGeneration, pollGatewayJob,
  downloadGatewayFile, getGatewayHistory,
} from "../../../service/gateway.service";

/**
 * Manages preflight validation, async generation lifecycle,
 * download and run history.
 */
export function useGatewayJob() {
  const [preflightReport,  setPreflightReport]  = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [jobId,            setJobId]            = useState(null);
  const [jobStatus,        setJobStatus]        = useState(null);
  const [progress,         setProgress]         = useState(0);
  const [generating,       setGenerating]       = useState(false);
  const [jobResult,        setJobResult]        = useState(null);
  const [history,          setHistory]          = useState([]);
  const [showHistory,      setShowHistory]      = useState(false);

  const buildRequest = (selectedTemplate, email, sourceType, dbName, sql, columnMappings, groupByColumn) => ({
    templateId:    selectedTemplate?.templateId,
    userEmail:     email,
    sourceType,
    dbName:        sourceType === "DB_QUERY" ? dbName : null,
    sql:           sourceType === "DB_QUERY" ? sql    : null,
    columnMappings,
    groupByColumn: groupByColumn || null,
  });

  const onRunPreflight = async (request, uploadedFile, onSuccess) => {
    setPreflightLoading(true);
    try {
      const res = await runPreflight(request, uploadedFile);
      setPreflightReport(res.data);
      onSuccess?.();
    } catch (e) {
      message.error("Pre-flight failed: " + e.message);
    } finally {
      setPreflightLoading(false);
    }
  };

  const onGenerate = async (request, uploadedFile, sftpId, saveName, onSuccess) => {
    setGenerating(true);
    setProgress(0);
    try {
      const req = { ...request, sftpId, mappingName: saveName, saveMappingAsDefault: false };
      const res = await startGeneration(req, uploadedFile);
      const jid = res.data.jobId;
      setJobId(jid);
      const final = await pollGatewayJob(jid, (pct, status, data) => {
        setProgress(pct);
        setJobStatus(status);
        setJobResult(data);
      });
      setJobResult(final);
      message.success(`Generated — ${final.totalRows} rows`);
      onSuccess?.();
    } catch (e) {
      message.error("Generation failed: " + (e.errorMessage || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const onDownload = async (jid) => {
    if (!jid) return;
    try {
      const { blob, filename } = await downloadGatewayFile(jid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      message.success(`Downloaded ${filename}`);
    } catch (e) {
      message.error("Download failed: " + e.message);
    }
  };

  const loadHistory = (email) => {
    getGatewayHistory(email).then((r) => {
      setHistory(r.data || []);
      setShowHistory(true);
    });
  };

  const resetJob = () => {
    setJobId(null);
    setJobStatus(null);
    setProgress(0);
    setJobResult(null);
    setPreflightReport(null);
  };

  return {
    preflightReport, preflightLoading,
    jobId, jobStatus, progress, generating, jobResult,
    history, showHistory, setShowHistory,
    buildRequest, onRunPreflight, onGenerate, onDownload,
    loadHistory, resetJob,
  };
}
